import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button } from '@common/Button';
import { LayoutSidebar, LayoutWorkspace } from '@common/layout';
import { DocumentLoader } from '@common/hooks/useDocument';
import { DocumentEvent } from '@common/hooks/types';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useDispatch, useSelector } from 'react-redux';
import { State, bucketActions, flowActions } from '@store/index';
import { Stack } from '@mui/material';
import { Delete, EditOffOutlined, EditOutlined } from '@mui/icons-material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';

import { BucketSidebar } from './Sidebar';
import { IntercomLoader } from '@components/Modals/Loaders/Intercom/IntercomLoader';
import { getLoader, GetLoaderParams, LoaderConfiguration } from '@requests/loaders';
import { RemoveLoader } from '@components/Modals/Loaders/RemoveLoader';
import { ContentfulLoader } from '@components/Modals/Loaders/Contentful/ContentfulLoader';
import { BucketFiles } from './BucketFiles';
import { DevdocsLoader } from '@components/Modals/Loaders/Devdocs/DevdocsLoader';
import { Resource } from '@store/types';
import { GuruLoader } from '@components/Modals/Loaders/Guru/GuruLoader';
import { ServiceNavigation } from '@components/Workspace/Navigation';
import { usePermissions } from '@common/hooks/usePermissions';
import { RequestError } from '@requests/common';
import { SlackLoader } from '@components/Modals/Loaders/Slack/SlackLoader';

import {
  deleteObjectsFromBucket,
  getBucket,
  getBucketFiles,
  GetBucketFilesParams,
  getEmbeddedFiles,
  TelnyxBucketFile,
} from '@requests/buckets';

const Container = styled.div`
  height: calc(100vh - 61px);
`;

const LayoutContainer = styled.div`
  display: flex;
  align-items: flex-start;
  height: calc(100vh - 61px);
`;

interface Props {
  documentLoader: DocumentLoader;
}

export const Bucket: React.FC<Props> = ({ documentLoader }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { service_id, bucketName } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { admin, permissions } = usePermissions(service_id as string, Resource.BUCKET);

  const [selectedFiles, setSelectedFiles] = useState<GridRowSelectionModel>([]);
  const [loaderSettingsOpen, setLoaderSettingsOpen] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [removeLoader, setRemoveLoader] = useState<string | null>(null);

  const editBucketFiles = useSelector((state: State) => state.flow.editBucketFiles);
  const clearBucket = useSelector((state: State) => state.flow.clearBucket);
  const services = useSelector((state: State) => state.flow.services);
  const taskId = useSelector((state: State) => state.buckets.task_id);
  const service = services.find((service) => service.service_id === service_id);

  const allowUpdate = admin || permissions?.update;
  const allowDelete = admin || permissions?.delete;

  const embeddingsLoading = documentLoader.documents.some((document) => document.event === DocumentEvent.Embed);

  const {
    data: loader,
    status: loaderStatus,
    error: loaderError,
  } = useQuery<LoaderConfiguration, RequestError, LoaderConfiguration, [string, string, GetLoaderParams]>({
    queryKey: ['globalFiles', 'bucket-loader', { service_id: service_id as string, bucket_name: bucketName as string }],
    queryFn: getLoader,
    enabled: Boolean(service_id && bucketName),
    staleTime: 30000,
    refetchInterval: 30000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const isSlackLoaderRunning =
    loader?.loader === 'slack' && (!loader?.last_refreshed || loader?.last_refreshed?.status === 'running');

  const isRefreshIntervalShort =
    embeddingsLoading || documentLoader.loading || clearBucket || taskId || isSlackLoaderRunning;

  const { data: bucket = null, isLoading: isBucketLoading } = useQuery({
    queryKey: ['globalFiles', 'bucket', { service_id, bucket_name: bucketName }],
    queryFn: getBucket,
    enabled: Boolean(service_id && bucketName),
  });

  const { data: embeddingFiles = [], isFetching: isQueueFetching } = useQuery({
    queryKey: [
      'globalFiles',
      'embedding-files',
      { service_id: service_id as string, bucket_name: bucketName as string },
    ],
    queryFn: getEmbeddedFiles,
    enabled: Boolean(bucketName),
    staleTime: isRefreshIntervalShort ? 2000 : 10000,
    refetchInterval: isRefreshIntervalShort ? 2000 : 10000,
  });

  const {
    data = [],
    status: fileStatus,
    error: filesError,
  } = useQuery<TelnyxBucketFile[], RequestError, TelnyxBucketFile[], [string, string, GetBucketFilesParams]>({
    queryKey: [
      'globalFiles',
      'files',
      { service_id: service_id as string, bucket_name: bucketName as string, region: bucket?.region as string },
    ],
    queryFn: getBucketFiles,
    enabled: Boolean(bucketName && bucket?.region),
    staleTime: isRefreshIntervalShort ? 2000 : 30000,
    refetchInterval: isRefreshIntervalShort ? 2000 : 30000,
    retry: false,
  });

  const isFilesLoading = fileStatus === 'pending' && Boolean(bucketName && bucket?.region);

  useEffect(() => {
    if (!isBucketLoading && !bucket?.name) navigate(`/${service_id}/buckets`);
  }, [bucket, isBucketLoading]);

  useEffect(() => {
    dispatch(bucketActions.setTaskId(null));
    documentLoader.setDocuments([]);
  }, []);

  const isApiBucket = false;
  // const isApiBucket = spec?.bucket_name === bucketName;

  const { mutateAsync: deleteObjects, status: deleteStatus } = useMutation({
    mutationFn: deleteObjectsFromBucket,
    onSuccess: (data) => {
      if (!data) return;

      // invalidate all file queries
      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });

      if (data?.error) {
        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to delete selected files!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      if (editBucketFiles) dispatch(flowActions.setEditBucketFiles(null));
      enqueueSnackbar('All selected files deleted succesfully!', { variant: 'success', autoHideDuration: 3000 });
    },
  });

  const isDeleteObjectsLoading = deleteStatus === 'pending';

  const deleteSelectedRows = () => {
    enqueueSnackbar('Deleting selected files...', {
      variant: 'info',
      autoHideDuration: 3000,
    });

    if (bucket) {
      deleteObjects({
        service_id: service_id as string,
        bucketName: bucketName as string,
        region: bucket.region,
        objectNames: selectedFiles.map((item) => item) as string[],
      });
    }
  };

  return (
    <LayoutWorkspace
      header={service?.service_name || 'Workspace'}
      body={
        <LayoutContainer>
          {loaderSettingsOpen === 'intercom' && (
            <IntercomLoader
              page={page}
              setPage={setPage}
              open={loaderSettingsOpen}
              setOpen={setLoaderSettingsOpen}
              loader={loader}
              documentLoader={documentLoader}
            />
          )}
          {loaderSettingsOpen === 'contentful' && (
            <ContentfulLoader
              page={page}
              setPage={setPage}
              open={loaderSettingsOpen}
              setOpen={setLoaderSettingsOpen}
              loader={loader}
              documentLoader={documentLoader}
            />
          )}
          {loaderSettingsOpen === 'devdocs' && (
            <DevdocsLoader
              page={page}
              setPage={setPage}
              open={loaderSettingsOpen}
              setOpen={setLoaderSettingsOpen}
              loader={loader}
              documentLoader={documentLoader}
            />
          )}
          {loaderSettingsOpen === 'guru' && (
            <GuruLoader
              page={page}
              setPage={setPage}
              open={loaderSettingsOpen}
              setOpen={setLoaderSettingsOpen}
              loader={loader}
              documentLoader={documentLoader}
            />
          )}
          {loaderSettingsOpen === 'slack' && (
            <SlackLoader
              page={page}
              setPage={setPage}
              open={loaderSettingsOpen}
              setOpen={setLoaderSettingsOpen}
              loader={loader}
              documentLoader={documentLoader}
            />
          )}
          <RemoveLoader open={removeLoader} setOpen={setRemoveLoader} setPage={setPage} loader={loader} />
          <ServiceNavigation />
          <Container>
            <LayoutSidebar
              body={
                <BucketFiles
                  selectedFiles={selectedFiles}
                  setSelectedFiles={setSelectedFiles}
                  documentLoader={documentLoader}
                  files={data}
                  filesError={filesError}
                  isLoading={Boolean(isDeleteObjectsLoading || isFilesLoading || isBucketLoading)}
                  embeddingFiles={embeddingFiles}
                />
              }
              leftSidebar={
                <BucketSidebar
                  isQueueFetching={Boolean(isQueueFetching && bucketName)}
                  loaderSettingsOpen={loaderSettingsOpen}
                  setLoaderSettingsOpen={setLoaderSettingsOpen}
                  loaderError={loaderError}
                  documentLoader={documentLoader}
                  page={page}
                  setPage={setPage}
                  files={Array.isArray(data) ? data : []}
                  fileStatus={fileStatus}
                  loader={loader}
                  loaderStatus={loaderStatus}
                  setRemoveLoader={setRemoveLoader}
                  isBucketLoading={isBucketLoading}
                />
              }
            />
          </Container>
        </LayoutContainer>
      }
      actions={
        editBucketFiles ? (
          <Stack direction='row' spacing={1.5}>
            <Button
              disabled={!data || data?.length === 0 || isApiBucket}
              startIcon={<EditOffOutlined sx={{ fontSize: '2.2ch !important' }} />}
              onClick={() => {
                setSelectedFiles([]);
                dispatch(flowActions.setEditBucketFiles(null));
              }}
            >
              Stop Edit
            </Button>
            <Button
              disabled={selectedFiles.length <= 0 || isApiBucket}
              customColor='error'
              startIcon={<Delete />}
              onClick={deleteSelectedRows}
            >
              Delete Selected
            </Button>
          </Stack>
        ) : (
          <Stack direction='row' spacing={1.5}>
            <Button
              disabled={!data || data?.length === 0 || isApiBucket || !allowUpdate}
              startIcon={<EditOutlined sx={{ fontSize: '2.2ch !important' }} />}
              onClick={() => dispatch(flowActions.setEditBucketFiles(bucketName as string))}
            >
              Bulk Edit
            </Button>
            <Button
              customColor='black'
              startIcon={<Delete />}
              disabled={isApiBucket || !allowDelete || isSlackLoaderRunning}
              onClick={() => dispatch(flowActions.setDeleteBucket(bucketName as string))}
            >
              Delete Bucket
            </Button>
          </Stack>
        )
      }
    />
  );
};
